import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    get_my_referral,
    get_my_referral_redemptions,
} from '../../store/reducers/dashboardReducer';
import {
    Gift, Copy, CheckCircle, Users, DollarSign,
    Clock, Share2, ChevronRight, Link as LinkIcon,
    AlertCircle,
} from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

const buildReferralLink = (code) =>
    `${window.location.origin}/register?ref=${code}`;

const buildShareMessage = (code, program) => {
    if (program?.shareMessage) return program.shareMessage.replace(/\{code\}/g, code);
    const reward = program?.rewardValue
        ? program.rewardType === 'fixed'
            ? `AED ${program.rewardValue}`
            : `${program.rewardValue}%`
        : 'exclusive rewards';
    return `Shop at Pikko Dubai and get ${reward} off your first order! Use my referral code: ${code} or sign up via: ${buildReferralLink(code)}`;
};

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const fmtReward = (program) => {
    if (!program?.rewardValue) return '—';
    return program.rewardType === 'fixed'
        ? `AED ${program.rewardValue}`
        : `${program.rewardValue}%`;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS = {
    pending:  { cls: 'bg-orange-50 text-orange-600 border-orange-200',  label: 'Pending'  },
    approved: { cls: 'bg-green-50  text-green-600  border-green-200',   label: 'Approved' },
    rejected: { cls: 'bg-red-50    text-red-500    border-red-200',     label: 'Rejected' },
};

const StatusBadge = ({ status }) => {
    const { cls, label } = STATUS[status] || STATUS.pending;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
            {label}
        </span>
    );
};

// ─── Stat card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, iconCls, bgCls }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${bgCls}`}>
            <Icon className={`w-5 h-5 ${iconCls}`} />
        </div>
        <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        </div>
    </div>
);

// ─── Share button ─────────────────────────────────────────────────────────────

const ShareBtn = ({ onClick, icon: Icon, label, cls }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-semibold transition-all active:scale-95 ${cls}`}
    >
        <Icon className="w-4 h-4" />
        {label}
    </button>
);

// ─── Empty history ────────────────────────────────────────────────────────────

const EmptyHistory = () => (
    <div className="flex flex-col items-center py-12">
        <Users className="w-9 h-9 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-500">No referrals yet</p>
        <p className="text-xs text-gray-400 mt-1">Your referral activity will appear here</p>
    </div>
);

// ─── Program disabled state ────────────────────────────────────────────────────

const ProgramDisabled = () => (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center py-16 px-4 text-center">
        <AlertCircle className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm font-semibold text-gray-600">Referral program is not active</p>
        <p className="text-xs text-gray-400 mt-1">Check back soon — we'll launch exciting referral rewards.</p>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const MyReferral = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector((s) => s.auth);
    const {
        referralCode,
        referralProgram,
        referralRedemptions,
        referralTotalEarned,
        referralTotalUses,
        referralPendingCount,
    } = useSelector((s) => s.dashboard);

    const [codeCopied, setCodeCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            dispatch(get_my_referral()),
            dispatch(get_my_referral_redemptions()),
        ]).finally(() => setLoading(false));
    }, [dispatch]);

    const code = referralCode?.code || '';
    const referralLink = code ? buildReferralLink(code) : '';
    const shareMessage = buildShareMessage(code, referralProgram);
    const programEnabled = referralProgram?.enabled !== false;

    const copyCode = () => {
        if (!code) return;
        navigator.clipboard.writeText(code).catch(() => {});
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2500);
    };

    const copyLink = () => {
        if (!referralLink) return;
        navigator.clipboard.writeText(referralLink).catch(() => {});
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2500);
    };

    const shareWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const shareFacebook = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(shareMessage)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const shareNative = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Shop at Pikko Dubai',
                text: shareMessage,
                url: referralLink,
            }).catch(() => {});
        } else {
            copyLink();
        }
    };

    if (loading) {
        return (
            <div className="space-y-3 animate-pulse">
                <div className="h-36 bg-gray-200 rounded-lg" />
                <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}
                </div>
                <div className="h-52 bg-gray-100 rounded-lg" />
            </div>
        );
    }

    if (!programEnabled && !referralCode) {
        return <ProgramDisabled />;
    }

    return (
        <div className="space-y-3">

            {/* ── Hero card ── */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-900 to-slate-700 px-5 py-6 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-white/60 text-[10px] font-semibold uppercase tracking-widest mb-1">
                            Referral Program
                        </p>
                        <h2 className="text-white text-xl font-black leading-tight">
                            Invite friends,<br />earn rewards
                        </h2>
                        {referralProgram?.rewardValue > 0 && (
                            <p className="text-white/70 text-[13px] mt-2">
                                Earn{' '}
                                <span className="text-orange-400 font-bold">
                                    {fmtReward(referralProgram)}
                                </span>{' '}
                                for every successful referral
                            </p>
                        )}
                        {referralProgram?.minOrder > 0 && (
                            <p className="text-white/50 text-[11px] mt-1">
                                Min. order: AED {referralProgram.minOrder}
                            </p>
                        )}
                    </div>
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Gift className="w-7 h-7 text-orange-400" />
                    </div>
                </div>

                {/* Member row */}
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                        Referral member: <span className="font-semibold text-gray-800">{userInfo?.name || 'Customer'}</span>
                    </p>
                    <span className="text-[11px] text-green-600 font-semibold bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">
                        Active
                    </span>
                </div>
            </div>

            {/* ── Stats ── */}
            <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
                <StatCard
                    icon={Users}
                    label="Total Referrals"
                    value={referralTotalUses}
                    iconCls="text-blue-500"
                    bgCls="bg-blue-50"
                />
                <StatCard
                    icon={DollarSign}
                    label="Total Earned"
                    value={`AED ${referralTotalEarned.toFixed(2)}`}
                    iconCls="text-green-500"
                    bgCls="bg-green-50"
                />
                <StatCard
                    icon={Clock}
                    label="Pending Rewards"
                    value={referralPendingCount}
                    iconCls="text-orange-500"
                    bgCls="bg-orange-50"
                />
            </div>

            {/* ── My referral code ── */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Your Referral Code
                    </span>
                </div>

                <div className="p-5 space-y-4">
                    {/* Code block */}
                    <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-50 border border-dashed border-gray-300 rounded-xl px-5 py-3.5 min-w-0">
                            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1">Code</p>
                            <p className="text-2xl font-black text-gray-900 font-mono tracking-[0.2em] truncate">
                                {code || '—'}
                            </p>
                        </div>
                        <button
                            onClick={copyCode}
                            disabled={!code}
                            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] font-bold transition-all flex-shrink-0 ${
                                codeCopied
                                    ? 'bg-green-100 text-green-700 border border-green-200'
                                    : 'bg-orange-500 text-white hover:bg-orange-600 active:scale-95'
                            }`}
                        >
                            {codeCopied
                                ? <><CheckCircle className="w-4 h-4" /> Copied!</>
                                : <><Copy className="w-4 h-4" /> Copy</>
                            }
                        </button>
                    </div>

                    {/* Referral link row */}
                    {referralLink && (
                        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                            <LinkIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <p className="text-[12px] text-gray-500 truncate flex-1 font-mono">
                                {referralLink}
                            </p>
                            <button
                                onClick={copyLink}
                                className={`text-[11px] font-bold px-3 py-1 rounded-full flex-shrink-0 transition ${
                                    linkCopied
                                        ? 'bg-green-100 text-green-600'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500'
                                }`}
                            >
                                {linkCopied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    )}

                    {/* Share buttons */}
                    <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2.5">
                            Share via
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <ShareBtn
                                onClick={shareWhatsApp}
                                icon={() => (
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                )}
                                label="WhatsApp"
                                cls="bg-[#25D366] text-white hover:bg-[#20bb5a]"
                            />
                            <ShareBtn
                                onClick={shareFacebook}
                                icon={() => (
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                )}
                                label="Facebook"
                                cls="bg-[#1877F2] text-white hover:bg-[#1665d8]"
                            />
                            <ShareBtn
                                onClick={shareNative}
                                icon={Share2}
                                label={typeof navigator !== 'undefined' && navigator.share ? 'Share' : 'Copy Link'}
                                cls="bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── How it works ── */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        How it works
                    </span>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-1 divide-x md:divide-x-0 md:divide-y divide-gray-100">
                    {[
                        { step: '1', title: 'Share your code', desc: 'Send your unique referral link to friends.' },
                        { step: '2', title: 'Friend signs up', desc: 'They register and place their first order.' },
                        { step: '3', title: 'You both earn', desc: `You earn ${fmtReward(referralProgram)} when approved.` },
                    ].map(({ step, title, desc }) => (
                        <div key={step} className="flex items-start gap-3 px-5 py-4">
                            <div className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                                {step}
                            </div>
                            <div>
                                <p className="text-[13px] font-bold text-gray-900">{title}</p>
                                <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Referral history ── */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Referral History
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {referralRedemptions.length} referral{referralRedemptions.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {referralRedemptions.length === 0 ? (
                    <EmptyHistory />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                        Date
                                    </th>
                                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                        Reward
                                    </th>
                                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                        Status
                                    </th>
                                    <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                                        Ref ID
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {referralRedemptions.map((r) => (
                                    <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {fmtDate(r.createdAt)}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                                            {r.rewardValue > 0 ? `AED ${r.rewardValue.toFixed(2)}` : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={r.status} />
                                        </td>
                                        <td className="px-4 py-3 text-right text-[11px] font-mono text-gray-400">
                                            #{r._id?.slice(-6).toUpperCase()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </div>
    );
};

export default MyReferral;
